import * as yup from "yup";

const REQUIRED_FIELD_MESSAGE = "Campo obrigatório";
const INVALID_PHONE_MESSAGE = "Número de telefone inválido";
const INVALID_NUIT_MESSAGE = "O NUIT deve ter 9 digitos";
const INVALID_PASSWORD_MESSAGE = "A senha deve conter letras e digitos apenas";
const NOT_A_NUMBER_MESSAGE = "Valor deve ser numérico";
const INVALID_MIN_NUMBER_MESSAGE = "Número não pode ser menor que zero";
const INVALID_SCORE_MESSAGE =
  "Pontuação Inválida. Insira um número entre 0 e 20";
const INVALID_BUSINESS_PLAN_SCORE_MESSAGE =
  "Pontuação Inválida. Insira um número entre 0 e 20";

const isRequiredString = yup
  .string()
  .typeError(REQUIRED_FIELD_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);
const isRequiredNumber = yup
  .number()
  .typeError(NOT_A_NUMBER_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);
const isNumber = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .nullable();
const isMonetary = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .nullable();
const isNullablePositiveInteger = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .integer("Número deve ser inteiro")
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .nullable();
const isValidPhoneNumber = yup.string().matches(/^8(2|3|4|5|6|7)[0-9]{7}$/, {
  message: INVALID_PHONE_MESSAGE,
  excludeEmptyString: true,
});
const isValidNuit = yup
  .string()
  .matches(/^[0-9]{9}$/, {
    message: INVALID_NUIT_MESSAGE,
    excludeEmptyString: true,
  })
  .nullable();
const isValidEmail = yup.string().email("Email inválido").nullable();
const isValidDate = yup
  .string()
  .matches(
    /^(0[1-9]|1\d|2\d|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/,
    "Data inválida. Use o formato dd/mm/aaaa"
  );

const isValidFundingRequest = yup
  .number()
  .max(1500000, "Montante não pode ser superior a 1.500.000 MZN")
  .nullable();

const isValidScore = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_SCORE_MESSAGE)
  .min(0, INVALID_SCORE_MESSAGE)
  .max(20, INVALID_SCORE_MESSAGE);

const isValidBusinessPlanScore = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .min(0, INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .max(20, INVALID_BUSINESS_PLAN_SCORE_MESSAGE);

const roleRules = yup.object({
  roleCode: yup.string().required(REQUIRED_FIELD_MESSAGE),
});

const passwordRules = yup.object({
  password: yup
    .string()
    .transform((x) => (x === "" ? undefined : x))
    .required(REQUIRED_FIELD_MESSAGE)
    .min(6, "A senha deve ter no mínimo 6 caracteres")
    .max(20, "A senha deve ter no máximo 20 caracteres")
    .matches(
      /^(?=.*\d)[^#&!~<>?=+*()-+=\/?@{}_$%]+$/,
      INVALID_PASSWORD_MESSAGE
    ),
  passwordConfirm: yup
    .string()
    .transform((x) => (x === "" ? undefined : x))
    .required(REQUIRED_FIELD_MESSAGE)
    .oneOf([yup.ref("password")], "As senhas devem ser iguais"),
});

const userSchema = yup.object({
  firstName: isRequiredString,
  lastName: isRequiredString,
  username: isRequiredString.concat(isValidPhoneNumber),
  email: isValidEmail,
});

const selfKnowledgeQuestionnaireSchema = yup.object({
  issue1: isRequiredString,
  issue2: isRequiredString,
  issue3: isRequiredString,
  issue4: isRequiredString,
  issue5: isRequiredString,
  issue6: isRequiredString,
  issue7: isRequiredString,
  issue8: isRequiredString,
  issue9: isRequiredString,
  issue10: isRequiredString,
  issue11: isRequiredString,
  issue12: isRequiredString,
  issue13: isRequiredString,
  issue14: isRequiredString,
  issue15: isRequiredString,
  issue16: isRequiredString,
  issue17: isRequiredString,
  issue18: isRequiredString,
  issue19: isRequiredString,
  issue20: isRequiredString,
  issue21: isRequiredString,
  issue22: isRequiredString,
  issue23: isRequiredString,
  issue24: isRequiredString,
  issue25: isRequiredString,
});

const basicCandidatureSchema = yup.object({
  dataUseConsent: isRequiredString,
  companyName: isRequiredString,
  companyHeadquarters: isRequiredString,
  nuit: isValidNuit.concat(isRequiredString),
  email: isValidEmail.concat(isRequiredString),
  startYearOfActivities: isRequiredNumber,
  legalFormCode: isRequiredString,
  contactPerson: isRequiredString,
  contactPersonRole: isRequiredString,
  phoneNumber: isValidPhoneNumber.concat(isRequiredString),
  expectedFundingAmountFromConecta: isMonetary,
});

const submitApplicationSchema = yup.object({
  identificationDocTypeCode: isRequiredString,
  otherIdentificationDocType: yup
    .string()
    .when("identificationDocTypeCode", {
      is: "0",
      then: yup.string().required("Especifique o tipo"),
    })
    .nullable(true),
  identificationDocNumber: isRequiredString,
  phoneNumber: isRequiredString.concat(isValidPhoneNumber),
  provinceId: isRequiredString,
  districtId: isRequiredString,
  address: isRequiredString,
  academicLevelCode: isRequiredString,
  speaksAndUnderstandsPortuguese: isRequiredString,
  writesInPortuguese: isRequiredString,
  businessName: isRequiredString,
  legalFormCode: isRequiredString,
  hasLicense: isRequiredString,
  usesAccounting: isRequiredString,
  businessManagerCode: isRequiredString,
  totalWorkers: isRequiredString,
  femaleWorkers: isRequiredString,
  maleWorkers: isRequiredString,
  femaleWorkers18To35: isRequiredString,
  maleWorkers18To35: isRequiredString,
  minSalary: isRequiredString,
  maxSalary: isRequiredString,
  salaryEvolution: isRequiredString,
  mainClients: isRequiredString,
  estimatedCurrentClients: isRequiredString,
  projectedClientsYear1: isRequiredString,
  projectedClientsYear2: isRequiredString,
  projectedClientsYear3: isRequiredString,
  lastYearTotalSales: isRequiredString,
  ecommercePlatformCode: isRequiredString,
  // orderSourceCode: isRequiredString,
  knowsPossibleEnvironmentalEffects: isRequiredString,
  knowsHealthAndSafetyAtWork: isRequiredString,
  hasReceivedRelevantBusinessPerformanceTraining: isRequiredString,
  confirmsAvailabilityForTraining: isRequiredString,
  hasSpecialNeedsForTraining: isRequiredString,
  isAvailableForTrainingOutsideOfResidence: isRequiredString,
});

const techEvalScoreSchema = yup.object({
  TE101: isValidScore,
  TE102: isValidScore,
  TE103: isValidScore,
  TE104: isValidScore,
  TE201: isValidScore,
  TE202: isValidScore,
  TE203: isValidScore,
  TE301: isValidScore,
  TE401: isValidScore,
  TE501: isValidScore,
  AE101: isValidScore,
  AE102: isValidScore,
  AE201: isValidScore,
});

const prototypeScoreSchema = yup.object({
  PF101: isValidScore,
  PD101: isValidScore,
  PD102: isValidScore,
  PC101: isValidScore,
  PC102: isValidScore,
  PU101: isValidScore,
  PU102: isValidScore,
  PR101: isValidScore,
  PS101: isValidScore,
  PM101: isValidScore,
  PP101: isValidScore,
});

const prototypeSchema = yup.object({
  platformUrl: isRequiredString,
  functionalityDescription: isRequiredString,
  additionalInfo: yup.string().nullable(),
});

const adminEvalScoreSchema = yup.object({
  AD101: isValidScore,
  AD102: isValidScore,
  AD103: isValidScore,
  AD104: isValidScore,
  AD201: isValidScore,
  AD202: isValidScore,
  AD203: isValidScore,
  AD301: isValidScore,
  AD401: isValidScore,
  AD501: isValidScore,
});

export {
  userSchema,
  roleRules,
  passwordRules,
  selfKnowledgeQuestionnaireSchema,
  basicCandidatureSchema,
  submitApplicationSchema,
  techEvalScoreSchema,
  adminEvalScoreSchema,
  prototypeSchema,
  prototypeScoreSchema,
};
